/**
 * services/portalApi.js — the signed-in part: accounts and bookings.
 *
 * Accounts are the same ones the Forge codebase uses: no password, just
 * a six-digit code emailed to you. The token lives in localStorage under
 * `makua_token` and is sent as a Bearer token.
 */
const BASE = (process.env.REACT_APP_API_URL || "http://localhost:5055/api").replace(/\/$/, "");
const TOKEN_KEY = "makua_token";
const MEMBER_KEY = "makua_member";

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
};
export const getMember = () => {
  try { return JSON.parse(localStorage.getItem(MEMBER_KEY) || "null"); } catch (_) { return null; }
};
export const signedIn = () => !!getToken();
export const signOut = () => {
  try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(MEMBER_KEY); } catch (_) {}
};
const remember = (token, member) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (member) localStorage.setItem(MEMBER_KEY, JSON.stringify(member));
  } catch (_) {}
};

async function request(path, { method = "GET", body } = {}) {
  const token = getToken();
  const res = await fetch(`${BASE}/portal${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  let payload = null;
  try { payload = await res.json(); } catch (_) {}

  if (res.status === 401) {
    signOut();
    const err = new Error(payload?.message || "Please sign in again.");
    err.status = 401;
    throw err;
  }
  if (!res.ok || payload?.success === false) {
    const err = new Error(payload?.message || "Something went wrong. Please try again.");
    err.status = res.status;
    throw err;
  }
  return payload?.data ?? {};
}

export const accountAPI = {
  /** New account: name + email, then a code by email. */
  register: (payload) => request("/auth/register", { method: "POST", body: payload }),
  /** Existing account: send me a code. */
  requestCode: (email) => request("/auth/request-otp", { method: "POST", body: { email } }),
  /** Finish either of the above. */
  verifyCode: async (email, otp) => {
    const data = await request("/auth/verify-otp", { method: "POST", body: { email, otp } });
    remember(data.token, data.member);
    return data;
  },
};

/** The same sign-in at Forge Retreat Colombia, and signing out everywhere. */
export const ssoAPI = {
  /** { url, targetName }: a one-time link that signs you in on the other site */
  handoff: (next) => request("/sso/handoff", { method: "POST", body: next ? { next } : undefined }),
  /** { email, from, here, hasAccount } */
  peek: (code) => request("/sso/peek", { method: "POST", body: { code } }),
  /** Signs in here (asks first when there's no account yet: consent). */
  consume: async (code, consent) => {
    const data = await request("/sso/consume", { method: "POST", body: { code, consent } });
    remember(data.token, data.member);
    return data;
  },
  logoutEverywhere: () => request("/sso/logout-everywhere", { method: "POST" }),
};

/** The community forum (one forum, shared with Forge): { enabled, url } */
export const communityAPI = { info: () => request("/community") };

/** Partners who sell Makua retreats for their clients. */
export const sellerAPI = {
  /** { seller: { name, company, terms, referralLink, totals, payment_visibility }, cancellationSummary } */
  me: () => request("/seller"),
  /** { reservations, totals } */
  list: () => request("/seller/reservations"),
  get: (ref) => request(`/seller/reservations/${encodeURIComponent(ref)}`).then((d) => d.reservation),
  book: (payload) => request("/seller/reservations", { method: "POST", body: payload }).then((d) => d.reservation),
  updateGuest: (ref, guestId, payload) =>
    request(`/seller/reservations/${encodeURIComponent(ref)}/guests/${guestId}`, { method: "PATCH", body: payload }).then((d) => d.reservation),
};

/** Preparation and integration courses (the shared library). */
export const coursesAPI = {
  /** [{ id, title, description, thumbnail, open, opensAt, waitingFor, progress: { done, total, complete } }] */
  list: () => request("/courses"),
  get: (id) => request(`/courses/${id}`),
  /** { progress } */
  done: (id, lessonId, done) => request(`/courses/${id}/lessons/${lessonId}/progress`, { method: "POST", body: { done } }),
};

export const memberAPI = {
  /** Everything this person has with us: retreats, workshops and orders. */
  account: () => request("/account"),
};

export const bookingAPI = {
  options: () => request("/booking/options"),
  mine: () => request("/booking"),
  create: (payload) => request("/booking", { method: "POST", body: payload }),
  update: (payload) => request("/booking", { method: "PATCH", body: payload }),
};

export const reservationsAPI = {
  create: (payload) => request("/reservations", { method: "POST", body: payload }),
  /** { reservations, diets } */
  mine: () => request("/reservations"),
  get: (ref) => request(`/reservations/${encodeURIComponent(ref)}`).then((d) => d.reservation),
  updateGuest: (ref, guestId, payload) =>
    request(`/reservations/${encodeURIComponent(ref)}/guests/${guestId}`, { method: "PATCH", body: payload }).then((d) => d.reservation),
  /** Stripe's payment page for what's due now ("now") or everything owed ("balance"): { url } */
  cardCheckout: (ref, amount = "now") =>
    request(`/reservations/${encodeURIComponent(ref)}/card-checkout`, { method: "POST", body: { amount } }),
  /** Back from Stripe: record the payment if it went through. { paid, status } */
  cardReturn: (ref, sessionId) =>
    request(`/reservations/${encodeURIComponent(ref)}/card-return`, { method: "POST", body: { session_id: sessionId } }),
};
